import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsOfUse = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Logo */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="inline-flex items-center">
            <img src="/qnc-logo.png" alt="QNC Co. Limited" className="h-12 w-auto" />
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Use</h1>
          <p className="text-sm text-gray-500 mb-8">Last Updated: November 2025</p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to QNC Co. Limited ("QNC," "we," "us," or "our"). By accessing or using our website <a href="https://qncconstructions.com" className="text-blue-600 hover:underline">qncconstructions.com</a>, you agree to be bound by these Terms of Use. If you do not agree, please discontinue use immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Eligibility</h2>
              <p className="text-gray-700 leading-relaxed">
                You must be at least 18 years old to use this website or request our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Services</h2>
              <p className="text-gray-700 leading-relaxed">
                QNC provides construction, renovation, project management, prefab steel building solutions, and related services. All services are subject to availability, contract approval, and site conditions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Website Use</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree not to engage in unlawful activity, attempt unauthorized access, upload malicious content, or use our materials commercially without permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Quotes & Contracts</h2>
              <p className="text-gray-700 leading-relaxed">
                Any estimate or quote provided is non-binding until confirmed in writing, may vary after site inspection, and is subject to material price changes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All website content is owned by QNC Co. Limited and protected under Jamaican copyright law.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                We are not responsible for the content, accuracy, or policies of external websites.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                To the fullest extent permitted by Jamaican law, QNC is not liable for losses due to website interruptions, errors, or reliance on information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify QNC for any misuse of the website.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                In the event of any dispute arising from or relating to these Terms of Use or your use of our services, the parties agree to first attempt to resolve the matter through good-faith negotiation.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                If the dispute cannot be resolved through negotiation within thirty (30) days, the parties agree to submit the dispute to binding arbitration in Kingston, Jamaica, in accordance with the Arbitration Act of Jamaica.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The arbitrator's decision shall be final and binding, and judgment may be entered in any court of competent jurisdiction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms at any time. Continued use means acceptance.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of Jamaica.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information</h2>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>QNC Co. Limited</strong>
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  141B Constant Spring Rd, Kingston, Jamaica
                </p>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Email: <a href="mailto:qncconstructions@gmail.com" className="text-blue-600 hover:underline">qncconstructions@gmail.com</a>
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Phone: 876-479-7139 / 876-784-7936
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;